import { Shield } from "lucide-react";
import { Link } from "react-router-dom";

const sections = [
	{
		title: "1. Information We Collect",
		content:
			"We collect information you provide directly to us when you create an account, including your name, email address, birth date, birth time, and birth location. We also collect usage data to improve our services.",
	},
	{
		title: "2. How We Use Your Information",
		content:
			"We use your personal information, including your birth data, solely to generate heliocentric astrological charts, interpretations, and reports. Your data is never sold to third parties. We use your email to send account-related communications and, if you opt in, updates about our services.",
	},
	{
		title: "3. Birth Data & Astrological Calculations",
		content:
			"Your birth date, time, and location are essential for generating accurate heliocentric charts. This information is stored securely and used exclusively for astrological calculations within the app. You may update or delete this information at any time from your profile.",
	},
	{
		title: "4. Payment Information",
		content:
			"All payment processing is handled by Stripe, a secure third-party payment processor. We do not store your full credit card details on our servers. Stripe's privacy policy governs the handling of your payment information.",
	},
	{
		title: "5. Data Storage & Security",
		content:
			"Your data is stored on secure servers with industry-standard encryption. We implement technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.",
	},
	{
		title: "6. Data Sharing",
		content:
			"We do not sell, trade, or rent your personal information to third parties. We may share anonymized, aggregated data for research or analytics purposes. We may disclose your information if required by law or to protect the rights and safety of our users.",
	},
	{
		title: "7. AI-Generated Interpretations",
		content:
			"Our app uses AI services (including Google and other providers) to generate astrological interpretations. Your birth data and chart data may be sent to these AI services to generate personalized content. These services are bound by their own privacy policies.",
	},
	{
		title: "8. Your Rights",
		content:
			"You have the right to access, correct, or delete your personal data at any time. You can update your profile information from within the app, or contact us to request deletion of your account and all associated data. Upon account deletion, your data is soft-deleted and permanently removed after 30 days.",
	},
	{
		title: "9. Cookies & Tracking",
		content:
			"Our web application may use cookies to manage your session and preferences. We may use analytics tools to understand how users interact with our app. This data is collected anonymously and used only to improve the user experience.",
	},
	{
		title: "10. Children's Privacy",
		content:
			"Our services are not directed to individuals under the age of 13. We do not knowingly collect personal information from children under 13. If we become aware that a child under 13 has provided personal information, we will take steps to delete such information.",
	},
	{
		title: "11. Changes to This Policy",
		content:
			"We may update this Privacy Policy from time to time. We will notify you of any significant changes via email or an in-app notification. Your continued use of the app after changes constitutes acceptance of the updated policy.",
	},
	{
		title: "12. Contact Us",
		content:
			"If you have questions about this Privacy Policy or how we handle your data, please contact us at privacy@heliocentricapp.com.",
	},
];

const PrivacyPolicy = () => {
	return (
		<div className="min-h-screen bg-slate-50 py-10 px-4">
			<div className="max-w-3xl mx-auto">
				{/* Header card */}
				<div
					className="rounded-2xl p-6 mb-8 text-white"
					style={{
						background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)",
					}}
				>
					<div className="flex items-center gap-3 mb-3">
						<div className="bg-white/15 rounded-xl p-2.5">
							<Shield size={22} className="text-yellow-400" />
						</div>
						<h1 className="text-2xl font-bold">Privacy Policy</h1>
					</div>
					<p className="text-white/70 text-sm mb-2">Last updated: March 2026</p>
					<p className="text-white/85 text-sm leading-relaxed">
						Your privacy is important to us. This policy explains how Heliocentric
						Astrology collects, uses, and protects your personal information.
					</p>
				</div>

				{/* Sections */}
				<div className="bg-white rounded-2xl shadow-sm border border-slate-100 divide-y divide-slate-100 mb-6">
					{sections.map(({ title, content }) => (
						<div key={title} className="px-6 py-5">
							<h2 className="text-sm font-bold text-slate-900 mb-2">{title}</h2>
							<p className="text-sm text-slate-500 leading-relaxed">{content}</p>
						</div>
					))}
				</div>

				{/* Footer notice */}
				<div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 flex gap-3 items-start mb-8">
					<span className="text-indigo-500 mt-0.5 text-base">ℹ️</span>
					<p className="text-xs text-indigo-800 leading-relaxed">
						By using Heliocentric Astrology, you agree to the collection and use of
						information in accordance with this policy.
					</p>
				</div>

				{/* Navigation links */}
				<div className="flex gap-4 text-sm text-slate-500 justify-center">
					<Link to="/" className="hover:text-slate-800 transition-colors">
						← Back to Home
					</Link>
					<span className="text-slate-300">|</span>
					<Link
						to="/terms-conditions"
						className="hover:text-slate-800 transition-colors"
					>
						Terms &amp; Conditions
					</Link>
				</div>
			</div>
		</div>
	);
};

export default PrivacyPolicy;
